import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './BlogSection.css';
import { useBrandHomePageBlogPosts } from '../../../hooks/useBrandHomePageContent';
import { usePaginatedBlogXML } from '../../../hooks/useBlogXML';
import { HandDrawnButton } from '../../shared/HandDrawnButton/HandDrawnButton';

interface BlogPost {
  id: number;
  title: string;
  author: string;
  date: string;
  link: string;
  hoverImage?: string;
}

const blogPosts: BlogPost[] = [
  {
    id: 1,
    title: "Awasi Artist Immersion – Iliana Diaz Lopez",
    author: "by Gabriel O'rorke",
    date: "05/25/2025",
    link: "/blog/awasi-artist-immersion-iliana-diaz-lopez",
    hoverImage: "https://images.ctfassets.net/hz1mviiqoqof/4oJe7GOzAuSyArR50UdSDo/6126294ba16ff4b6cf959467672bf741/81f807eaaff0afbe8099e75dbd63fe9d854a5cdb.png"
  },
  {
    id: 2,
    title: "Art & Sustainability at Awasi",
    author: "by Gabriel O'rorke",
    date: "05/25/2025",
    link: "/blog/art-sustainability-at-awasi",
    hoverImage: "https://images.ctfassets.net/hz1mviiqoqof/81jpjprgBrUARz0qRTiJf/f2ecd988f1d51420db560693cbc5fbe0/2cfc147434c8dee6b57fda7f259a68b7167f3be3.png"
  },
  {
    id: 3,
    title: "Awasi Artist Immersion – Fabiano Al Makul",
    author: "by Gabriel O'rorke",
    date: "05/25/2025",
    link: "/blog/awasi-artist-immersion-fabiano-al-makul",
    hoverImage: "https://images.ctfassets.net/hz1mviiqoqof/16LnrjAzJAVHC900Rg47rz/f1accf0034dcd94c20ce549baa775360/fb01c0e8585a2c7882c0e052e03fb855818f468d.png"
  },
  {
    id: 4,
    title: "Awasi Artist Immersion – Rainer Gross",
    author: "by Gabriel O'rorke",
    date: "05/25/2025",
    link: "/blog/awasi-artist-immersion-rainer-gross",
    hoverImage: "https://images.ctfassets.net/hz1mviiqoqof/16LnrjAzJAVHC900Rg47rz/f1accf0034dcd94c20ce549baa775360/fb01c0e8585a2c7882c0e052e03fb855818f468d.png"
  },
  {
    id: 5,
    title: "Introducing Awasi's Art Curator: Alexandra van Peborgh",
    author: "by Gabriel O'rorke",
    date: "05/25/2025",
    link: "/blog/introducing-awasis-art-curator-alexandra-van-peborgh",
    hoverImage: "https://images.ctfassets.net/hz1mviiqoqof/1zsd1eHA74dEnAG8e7Deor/80b00aadff756f3803f09ff87a113ec4/a8a49c18cc63bb8571d68aa2cb70530693cd119d.png"
  }
];

interface BlogItemProps {
  post: BlogPost;
  isLast: boolean;
}

const BlogItem: React.FC<BlogItemProps> = ({ post, isLast }) => {
  const [showFloatingImage, setShowFloatingImage] = React.useState(false);
  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });
  const floatingImageRef = React.useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    // Use fixed positioning relative to viewport instead of container
    setMousePosition({
      x: e.clientX,
      y: e.clientY
    });
  };

  const handleMouseEnter = () => {
    if (post.hoverImage) {
      setShowFloatingImage(true);
    }
  };

  const handleMouseLeave = () => {
    setShowFloatingImage(false);
  };

  return (
    <article className="blog-section__item">
      <Link
        to={post.link}
        className="blog-section__item-link"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <h3 className="blog-section__item-title">{post.title}</h3>
        <div className="blog-section__item-meta">
          <div className="blog-section__item-date">{post.date}</div>
        </div>
      </Link>
    </article>
  );
};

export const BlogSection: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { posts: contentfulPosts, loading } = useBrandHomePageBlogPosts();
  const { posts: xmlPosts, loading: xmlLoading } = usePaginatedBlogXML(1, 5);

  // Show loading state
  if (xmlLoading && loading) {
    return (
      <section className="blog-section" data-scroll-section>
        <h2 className="blog-section__title">
          <p>{t('pages/home:blog.title')}</p>
        </h2>
        <div className="blog-section__loading">{t('pages/home:blog.loading')}</div>
      </section>
    );
  }

  // Format date helper
  const formatDate = (dateStr: string | Date) => {
    const date = new Date(dateStr);
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  // Priority: XML posts > Contentful posts > Static posts
  const displayPosts = xmlPosts.length > 0
    ? xmlPosts.map((post, index) => ({
      id: parseInt(post.id) || index + 1,
      title: post.title,
      author: t('pages/home:blog.byAuthor', { author: post.author }),
      date: formatDate(post.publishDate),
      link: `/blog/${post.slug}`,
      hoverImage: blogPosts[index]?.hoverImage // Use static hover images for now
    }))
    : contentfulPosts.length > 0
      ? contentfulPosts.map((post, index) => ({
        id: index + 1,
        title: String(post.title),
        author: t('pages/home:blog.byAuthor', { author: String(post.author) }),
        date: formatDate(String(post.date)),
        link: post.link,
        hoverImage: blogPosts[index]?.hoverImage // Use static hover images for now
      }))
      : blogPosts;

  const handleSeeOurBlog = () => {
    navigate('/blog/');
  };

  return (
    <section className="blog-section" data-scroll-section>
      <h2 className="blog-section__heading">
        {t('pages/home:blog.title')}
      </h2>

      <div className="blog-section__posts">
        {displayPosts.slice(0, 5).map((post, index) => (
          <BlogItem
            key={post.id}
            post={post}
            isLast={index === 4}
          />
        ))}
      </div>

      <div className="blog__buttons">
        <HandDrawnButton
          text={t('pages/home:blog.cta')}
          onClick={handleSeeOurBlog}
          variant="brand"
          color="awasi"
          animation="stroke-to-fill"
          size="normal"
          className="blog-section__button"
        />
      </div>
    </section>
  );
};